import React, { FocusEventHandler } from 'react';
import { styled } from '@mui/material/styles';

import { FormHelperText } from '@mui/material';
import UIKitRadioButton from './RadioButton';
import { OptionValue } from '../../services/types/Misc';
import { ThemeColor } from '../theme';
import { uikitPalette } from '../theme/uikitPalette';

interface UIKitRadioGroupProps {
  name: string;
  elements: OptionValue[];
  value: number;
  onChange: (value: number) => void;
  onBlur?: FocusEventHandler;
  direction?: 'horizontal' | 'vertical';
  color?: ThemeColor;
  className?: string;
  style?: React.CSSProperties;
  label?: string;
  error?: boolean;
  helperText?: string;
}

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'direction',
})<{ direction: 'horizontal' | 'vertical' }>(({ direction }) => ({
  display: 'flex',
  flexDirection: direction === 'vertical' ? 'column' : 'row',
}));

const Label = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isError',
})<{ isError?: boolean }>(({ isError }) => ({
  display: 'block',
  marginBottom: 10,
  marginRight: 20,
  alignSelf: 'center',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  color: isError ? uikitPalette.error.main : '#004E70',
}));

const Item = styled('div')({
  padding: '11px 11px 11px 0',
});

const StyledHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== 'isError',
})<{ isError?: boolean }>(({ isError }) => ({
  color: isError ? `${uikitPalette.error.main} !important` : uikitPalette.secondary.main,
  marginTop: -15,
  fontSize: 12,
  lineHeight: 1.4,
}));

const UIKitRadioGroup: React.FC<UIKitRadioGroupProps> = ({
  name,
  elements,
  value,
  onChange,
  onBlur,
  direction = 'horizontal',
  color = 'primary',
  className,
  style,
  label,
  error,
  helperText,
}) => (
  <div style={style}>
    <Container direction={direction} className={className}>
      {label && <Label isError={!!error}>{label}</Label>}
      {elements.map((option) => (
        <Item key={option.id}>
          <UIKitRadioButton
            label={option.text}
            name={name}
            value={option.id}
            checked={value === option.id}
            onChange={() => onChange(option.id)}
            onBlur={onBlur}
            disabled={option.disabled}
            color={color}
          />
        </Item>
      ))}
    </Container>
    {helperText && <StyledHelperText isError={!!error}>{helperText}</StyledHelperText>}
  </div>
);

export default UIKitRadioGroup;
